import filter from 'lodash/filter';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';

import { getShowroomFiltersInfo } from 'Apollo/Store/Showroom/read';
import {
	GetShowroomSectionFiltersUserViewQuery,
	ShowroomFilterValue,
	ShowroomFilterValueInfoFragment,
	ShowroomFilterGroupsInfoFragment
} from 'generated/graphql';

import { ShowroomFilterTree, ShowroomFilterTreeValue } from 'Types/showroom';

export const buildShowroomFiltersTree = (rawData: GetShowroomSectionFiltersUserViewQuery['showroomSectionFilters']): ShowroomFilterTree[] => {
	const topFilters = filter(rawData, filter => filter.groups.length === 1);

	const buildTreeValues = (value: ShowroomFilterValue, prevGroups: string[], parent?: ShowroomFilterTreeValue): ShowroomFilterTreeValue => {
		const filterTreeValue: ShowroomFilterTreeValue = {
			value,
			childs: [],
			parent: parent ?? null
		};

		const groupList = [...prevGroups, value.value];
		const groupLength = groupList.length;
		const group = groupList.join('_');

		const filterChilds = find(rawData, filter => filter.groups.length === groupLength && filter.groups.join('_') === group);

		if (filterChilds)
			filterTreeValue.childs = [...filterChilds.values]
				.sort(({ value }, { value: value2 }) => value.toLowerCase().localeCompare(value2.toLowerCase()))
				.map(value => buildTreeValues(value, groupList, filterTreeValue));
		return filterTreeValue;
	};

	const trees = topFilters.map(topFilter => ({
		id: topFilter.id,
		name: topFilter.groups[0],
		childs: [...topFilter.values]
			.sort(({ value }, { value: value2 }) => value.toLowerCase().localeCompare(value2.toLowerCase()))
			.map(value => buildTreeValues(value, topFilter.groups))
	}));
	return filter(trees, tree => tree.childs.length > 0);
};

const getSelectedValuesTopFilterGroup = (selectedValues: ShowroomFilterValueInfoFragment[]) => {
	const groupIds = uniq(selectedValues.map(selectedValue => selectedValue.groupId));
	const buckets: ShowroomFilterGroupsInfoFragment[] = getShowroomFiltersInfo(groupIds);
	return reduce(
		buckets,
		(acc, bucket) => {
			acc[bucket.id] = bucket.groups[0];
			return acc;
		},
		{}
	);
};

export const searchMedias = (selectedValues: ShowroomFilterValueInfoFragment[]) => {
	const firstGroups = getSelectedValuesTopFilterGroup(selectedValues);
	const filterByCategory = groupBy(selectedValues, selectedValue => firstGroups[selectedValue.groupId]);
	const filteredCategories: Record<string, string[]> = {};
	Object.entries(filterByCategory).forEach(([category, filters]) => {
		const categoryMedias = new Set<string>();
		filters.forEach(filter => {
			filter.mediaIds.forEach(mediaId => {
				categoryMedias.add(mediaId);
			});
		});
		filteredCategories[category] = Array.from(categoryMedias);
	});
	return intersection(...Object.values(filteredCategories));
};

export const updateFilterValues = (
	buckets: GetShowroomSectionFiltersUserViewQuery['showroomSectionFilters'],
	selectedValues: ShowroomFilterValueInfoFragment[]
) => {
	const selectedTopFilterGroups = getSelectedValuesTopFilterGroup(selectedValues);
	const selectedValuesIds = new Set<string>();
	selectedValues.forEach(({ id }) => selectedValuesIds.add(id));
	return buckets.map(filterBucket => {
		// Do a search for each selected bucket in other trees except this one (all selected filters except the one from the bucket)
		const otherFiltersSelected = filter(
			selectedValues,
			selectedValue => selectedTopFilterGroups[selectedValue.groupId] !== filterBucket.groups[0]
		);
		let values: ShowroomFilterValue[] = [];
		if (isEmpty(otherFiltersSelected)) values = [...filterBucket.values];
		else {
			const filteredMediaIds = searchMedias(otherFiltersSelected);
			values = reduce(
				filterBucket.values,
				(acc, value) => {
					const newMediaIds = intersection(value.mediaIds, filteredMediaIds);
					if (newMediaIds.length === 0 && !selectedValuesIds.has(value.id)) return acc;
					const newValue = {
						...value,
						mediaIds: newMediaIds
					};
					acc.push(newValue);
					return acc;
				},
				[]
			);
			values = orderBy(values, value => value.mediaIds.length, 'desc');
		}
		return {
			...filterBucket,
			values
		};
	});
};
